//import * as XLSX from 'xlsx-style';
import { ExportColumn } from '../template/exportReportTemplate';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const INVALID_FILE_CHARS = /[<>:"/\\|?*\x00-\x1F]/g;

const sanitizeFileName = (name: string) =>
    (name || 'report').replace(INVALID_FILE_CHARS, '_').trim();

const normalizeValue = (value: unknown): string | number | boolean => {
    if (value === null || value === undefined) {
        return '';
    }
    return value as string | number | boolean;
};

const protectCsvFormula = (value: string): string => {
    // Prevent CSV formula execution when opened in spreadsheet tools.
    if (/^[=+\-@]/.test(value)) {
        return `'${value}`;
    }
    return value;
};

const toCsvCell = (value: unknown): string => {
    const raw = String(normalizeValue(value));
    const safe = protectCsvFormula(raw);
    const escaped = safe.replace(/"/g, '""');
    return `"${escaped}"`;
};


export const printReport = async (
    data: any[],
    exportColumns: ExportColumn[],
    reportTitle: string,
    reportDate: string,
    pageSize: "a4" | "letter",
    orientation: "portrait" | "landscape") => {

    const doc = new jsPDF({
        orientation,
        unit: 'mm',
        format: pageSize,
    });

    // Draw Report Title and Date in the same row
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(0, 0, 128); // Dark blue
    const pageWidth = doc.internal.pageSize.getWidth();
    const tableMargin = 10;
    const tableWidth = pageWidth - 2 * tableMargin;
    doc.rect(tableMargin, 10, tableWidth, 10, 'F');
    doc.text(reportTitle, pageWidth / 2, 17, { align: "center" });
    doc.setFontSize(10);
    doc.text(`Updated at: ${reportDate}`, pageWidth - tableMargin - 1, 16, { align: "right" });

    // Prepare headers and body from exportColumns and data
    const groupHeaders: any[] = [];
    const columnHeaders: string[] = [];
    const body = data.map((row) => {
        const rowData: any[] = [];
        exportColumns.forEach((group) => {
            group.columns.forEach((col) => {
                rowData.push(normalizeValue(row[col.field]));
            });
        });
        return rowData;
    });

    exportColumns.forEach((group) => {
        groupHeaders.push({ content: group.groupName, colSpan: group.columns.length, styles: { halign: 'center', fillColor: [0, 0, 128], textColor: 255, fontStyle: 'bold' } });
        group.columns.forEach((col) => {
            columnHeaders.push(col.title);
        });
    });

    // Define column widths
    const columnStyles: { [key: number]: { cellWidth: 'auto' | number } } = {};
    let colIndex = 0;
    exportColumns.forEach((group) => {
        group.columns.forEach((col) => {
            columnStyles[colIndex++] = { cellWidth: 'auto' }; // Adjust 'auto' to specific width if needed
        });
    });

    // Generate table using autoTable with grouping headers and column widths
    autoTable(doc, {
        head: [groupHeaders, columnHeaders],
        body,
        startY: 30,
        styles: { fontSize: 7, cellPadding: 2, lineWidth: 0.1, lineColor: [221, 221, 221] }, // Light grey border for all cells
        headStyles: { fillColor: [125, 166, 222], textColor: 255, fontSize: 6, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [230, 230, 250] },
        margin: { left: tableMargin, right: tableMargin },
        columnStyles,
        didDrawPage: (data) => {
            // Draw border
            doc.setLineWidth(0.5);
            doc.setDrawColor(221, 221, 221); // Light grey border
            doc.rect(tableMargin, 10, tableWidth, doc.internal.pageSize.getHeight() - 20);
        }
    });

    doc.save(`${sanitizeFileName(reportTitle)}.pdf`);
};






// Helper for borders
const getBorder = () => ({
    top: { style: 'thin' as const, color: { argb: 'FFDDDDDD' } },  // Light grey
    bottom: { style: 'thin' as const, color: { argb: 'FFDDDDDD' } },  // Light grey
    left: { style: 'thin' as const, color: { argb: 'FFDDDDDD' } },  // Light grey
    right: { style: 'thin' as const, color: { argb: 'FFDDDDDD' } }  // Light grey
});

export const exportToExcel = async (
    data: any[],
    exportColumns: ExportColumn[],
    reportTitle: string,
    reportDate: string) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Report');

    const totalCols = exportColumns.reduce((sum, group) => sum + group.columns.length, 0);
    let currentRow = 1;

    // ===== Report Title =====
    worksheet.mergeCells(currentRow, 1, currentRow, totalCols);
    const titleCell = worksheet.getCell(currentRow, 1);
    titleCell.value = reportTitle;
    titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
    titleCell.alignment = { horizontal: 'center' };
    titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF000080' } // Dark Blue
    };
    titleCell.border = getBorder();
    currentRow++;

    // ===== Report Date =====
    worksheet.mergeCells(currentRow, 1, currentRow, totalCols);
    const dateCell = worksheet.getCell(currentRow, totalCols);
    dateCell.value = `Updated at: ${reportDate}`;
    dateCell.font = { italic: true };
    dateCell.alignment = { horizontal: 'right' };
    currentRow++;

    // ===== Empty Spacer Row =====
    currentRow++;

    // ===== Group Headers =====
    const groupHeaderRow = worksheet.getRow(currentRow);
    let startCol = 1;
    exportColumns.forEach((group, groupIndex) => {
        const endCol = startCol + group.columns.length - 1;
        worksheet.mergeCells(currentRow, startCol, currentRow, endCol);
        const cell = groupHeaderRow.getCell(startCol);
        cell.value = group.groupName;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: groupIndex % 2 === 0 ? 'FF000080' : 'FF7DA6DE' } // Light Blue/Dark Blue
        };
        cell.border = getBorder();
        for (let i = startCol; i <= endCol; i++) {
            groupHeaderRow.getCell(i).border = getBorder();
        }
        startCol = endCol + 1;
    });
    currentRow++;

    // ===== Column Headers =====
    const columnHeaderRow = worksheet.getRow(currentRow);
    let colIndex = 1;
    exportColumns.forEach((group) => {
        group.columns.forEach((col) => {
            const cell = columnHeaderRow.getCell(colIndex++);
            cell.value = col.title;
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7DA6DE' } };
            cell.border = getBorder();
        });
    });
    currentRow++;

    // ===== Data Rows =====
    data.forEach((rowData, i) => {
        const row = worksheet.getRow(currentRow++);
        let colPos = 1;
        exportColumns.forEach((group) => {
            group.columns.forEach((col) => {
                const cell = row.getCell(colPos++);
                cell.value = normalizeValue(rowData[col.field]);
                cell.alignment = { horizontal: 'left' };
                cell.border = getBorder();
                if (i % 2 === 0) {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFE6E6FA' } // Light gray
                    };
                }
            });
        });
    });

    // ===== Column Auto Width =====
    for (let i = 1; i <= totalCols; i++) {
        const headerLength = exportColumns
            .flatMap((group) => group.columns)
            .map((col) => String(col.title).length)[i - 1] || 10;
        const maxDataLength = Math.max(
            ...data.map((row) => {
                const col = exportColumns.flatMap((group) => group.columns)[i - 1];
                return String(normalizeValue(row?.[col?.field])).length;
            }),
            0
        );
        worksheet.getColumn(i).width = Math.min(40, Math.max(12, Math.max(headerLength, maxDataLength) + 2));
    }

    // ===== Save File =====
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${sanitizeFileName(reportTitle)}.xlsx`);
};



export const exportToCSV = async (
    data: any[],
    exportColumns: ExportColumn[],
    reportTitle: string,
    reportDate: string) => {
    let csvContent = '\uFEFF';

    // ===== Report Title =====
    csvContent += `${toCsvCell(reportTitle)}\n`;

    // ===== Report Date =====
    csvContent += `${toCsvCell(`Report Date: ${reportDate}`)}\n\n`;

    // ===== Column Headers (Single Row) =====
    const columnHeaders = exportColumns
        .map((group) => group.columns.map((col) => toCsvCell(col.title)).join(','))
        .join(',');
    csvContent += `${columnHeaders}\n`;

    // ===== Data Rows =====
    data.forEach((rowData) => {
        const row = exportColumns
            .map((group) =>
                group.columns
                    .map((col) => toCsvCell(rowData[col.field]))
                    .join(',')
            )
            .join(',');
        csvContent += `${row}\n`;
    });

    // ===== Trigger CSV download =====
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${sanitizeFileName(reportTitle)}.csv`);
};



